import { useContext, useEffect, useState } from 'react';
import { Box, Tab, Tabs } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import usePageTitle from '../../hooks/usePageTitle';

import { hasAccess } from '../../components/AccessGuard';
import { UserContext } from '../../App';
import { USER_GROUP_ADMIN } from '../../types/User';
import { TabsContainer } from '../../components/Tabs';
import DspAdGroupDictsTab from './DspAdGroupDictsTab';
import Block from '../../components/Block';
import Column from '../../components/Column';
import Heading from '../../components/Heading';
import Row from '../../components/Row';
import DspMacrosTab from './DspMacrosTab';
import DmaDataMappingTab from './DmaDataMappingTab';
import AudienceMappingTab from './AudienceMappingTab';
import DspInventorySupplyTab from './DspInventorySupplyTab';
import OemProgramsTab from './OemProgramsTab';
import OemsTab from './OemsTab';

const DataMappingPage = () => {
    const [value, setValue] = useState(0);
    const { userContext } = useContext(UserContext);
    const { tab: tabParam } = useParams();
    const navigate = useNavigate();

    const TABS = [
        {
            id: 0,
            slug: 'ad-groups',
            label: 'Ad Groups',
            component: (
                <DspAdGroupDictsTab
                    activeTab={value}
                    tab={{
                        id: 0,
                        description:
                            'Map ad group name values to inventory name values based on the type of targeting strategy.',
                    }}
                />
            ),
        },
        {
            id: 1,
            slug: 'dmas',
            label: 'DMAs',
            component: (
                <DmaDataMappingTab
                    activeTab={value}
                    tab={{
                        id: 1,
                        description:
                            'Visualize locations for targeting setup on the campaign creation page, and for creative decisioning for retargeting purposes.',
                    }}
                />
            ),
        },
        {
            id: 2,
            slug: 'dsp-macros',
            label: 'DSP Macros',
            component: (
                <DspMacrosTab
                    activeTab={value}
                    tab={{
                        id: 2,
                        description:
                            'DSP Macros are placeholder variables within URLs that are dynamically replaced with values from the DSP when an ad is served.',
                    }}
                />
            ),
        },
        {
            id: 3,
            slug: 'oems',
            label: 'OEMs',
            component: (
                <OemsTab
                    activeTab={value}
                    tab={{
                        id: 3,
                        description:
                            'Create OEMs and assign them to Brands to power our reporting filters and Advertiser OEM settings.',
                    }}
                />
            ),
        },
        {
            id: 4,
            slug: 'oem-programs',
            label: 'OEM Programs',
            component: (
                <OemProgramsTab
                    activeTab={value}
                    tab={{
                        id: 4,
                        description:
                            'Create OEM Programs and assign them to OEMs to power our reporting filters and Advertiser OEM settings.',
                    }}
                />
            ),
        },
        {
            id: 5,
            slug: 'dsp-audiences',
            label: 'DSP Audiences',
            component: (
                <AudienceMappingTab
                    activeTab={value}
                    tab={{
                        id: 5,
                        description:
                            'Used as a lookup table for our campaign creation process so that when a user selects a Targeting Strategy it can automatically select the audiences that are mapped to the corresponding Key Value(s) of that Strategy Type.',
                    }}
                />
            ),
        },
        {
            id: 6,
            slug: 'dsp-inventory',
            label: 'DSP Inventory Supply',
            component: (
                <DspInventorySupplyTab
                    activeTab={value}
                    tab={{
                        id: 6,
                        description:
                            'Used as a lookup table for our campaign creation process so that when a user assigns Inventory Sources they can do so by using our own custom categories and names which map to the equivalent DSP inventory source.',
                    }}
                />
            ),
        },
    ];

    // validate URL & update tab
    useEffect(() => {
        const validTab = TABS.find((tab) => tab.slug === tabParam);

        if (validTab) {
            setValue(validTab.id);
        } else {
            // invalid tab; redirect to default
            navigate('/dataMapping/ad-groups', { replace: false });
        }
    }, [tabParam, navigate]);

    // title
    usePageTitle({ segments: ['Data Mapping', TABS[value].label] });

    return (
        <Column gap={0} flex={1}>
            <Block>
                <Column gap={1}>
                    <Row spacing={2} justifyContent="space-between" alignItems="center">
                        <Heading>Data Mapping</Heading>

                        <Row justifyContent="flex-end"></Row>
                    </Row>
                </Column>
            </Block>

            <Column gap={0} sx={{ height: '100%' }}>
                <TabsContainer>
                    <Tabs
                        value={value}
                        onChange={(_, tabId) => navigate(`/dataMapping/${TABS[tabId].slug}`, { replace: false })}
                    >
                        {TABS?.map((tab, index) => {
                            return (
                                hasAccess(userContext, USER_GROUP_ADMIN) && (
                                    <Tab
                                        key={index}
                                        value={tab.id}
                                        label={tab.label}
                                        style={{ textTransform: 'none' }}
                                    />
                                )
                            );
                        })}
                    </Tabs>
                </TabsContainer>

                <Block>
                    {TABS?.map((tab, index) => {
                        return <Box key={index}>{tab.component} </Box>;
                    })}
                </Block>
            </Column>
        </Column>
    );
};

export default DataMappingPage;
